import {
  Coupon,
  CouponAppliesTo,
  CouponCurrency,
  CouponFormValues,
  CouponPayload,
} from '@/@types/Coupons';
import { couponCurrencyFromApi } from './couponScope';

/** Recorrência fora do escopo: o desconto vale só na primeira cobrança. */
export const COUPON_APPLIES_TO: CouponAppliesTo = 'first_charge';

/** O FormRequest diz `min:1`, mas a API recusa desconto abaixo de 500. */
export const COUPON_MIN_DISCOUNT_AMOUNT = 500;

/** Moeda usada para formatar o valor enquanto nenhuma está marcada. */
export const DEFAULT_COUPON_CURRENCY: CouponCurrency = 'BRL';

/** Valor inicial do campo ao ligar a regra; desligada, o comprador usa sem limite. */
const DEFAULT_MAX_USES_PER_BUYER = 1;

export const emptyCouponFormValues: CouponFormValues = {
  name: '',
  discountType: 'percentage',
  discount: null,
  discountAmount: null,
  offerIds: [],
  paymentMethods: [],
  currency: 'BRL',
  hasMinOrderValue: false,
  minOrderValue: 0,
  hasMaxUses: false,
  maxUses: null,
  hasMaxUsesPerBuyer: false,
  maxUsesPerBuyer: DEFAULT_MAX_USES_PER_BUYER,
  startsAt: '',
  expiresAt: '',
  status: 'active',
};

/** A API valida `decimal:2`, então o percentual precisa ir como "10.00". */
function toDecimalString(value: number | null): string | null {
  if (value === null || Number.isNaN(value)) {
    return null;
  }

  return Number(value).toFixed(2);
}

function toNullableInt(value: number | null): number | null {
  if (value === null || Number.isNaN(value)) {
    return null;
  }

  return Math.trunc(value);
}

function toNullableString(value: string): string | null {
  const trimmed = value.trim();

  return trimmed === '' ? null : trimmed;
}

/** Mesma normalização que o `prepareForValidation` da API faz. */
export function normalizeCouponCode(value: string): string {
  return value.replace(/\s/g, '').toUpperCase();
}

/**
 * A API devolve a mensagem crua do Laravel ("O name já está sendo utilizado"),
 * com o nome interno do campo. Só a de unicidade em `name` é reescrita — as de
 * formato e tamanho já vêm legíveis.
 */
export function isCodeTakenMessage(
  field: string,
  message: string | undefined,
): boolean {
  return (
    field === 'name' &&
    /utilizado|taken|already|existe|en uso|vergeben|déjà|già|既に/i.test(
      message ?? '',
    )
  );
}

/**
 * O PUT exige `min_order_value`, `max_uses_per_buyer` e `applies_to` sempre —
 * com o valor neutro quando o switch da regra está desligado. Em
 * `max_uses_per_buyer` o neutro é `null` (ilimitado), não 1.
 */
export function buildCouponPayload(values: CouponFormValues): CouponPayload {
  const isPercentage = values.discountType === 'percentage';

  return {
    name: normalizeCouponCode(values.name),
    discount_type: values.discountType,
    discount: isPercentage ? toDecimalString(values.discount) : null,
    discount_amount: isPercentage ? null : toNullableInt(values.discountAmount),
    // `null` não chega aqui: o formulário bloqueia o envio sem seleção.
    offer_ids: values.offerIds ?? [],
    payment_methods: values.paymentMethods ?? [],
    currency: values.currency,
    min_order_value: values.hasMinOrderValue
      ? Math.max(0, Math.trunc(values.minOrderValue || 0))
      : 0,
    max_uses: values.hasMaxUses ? toNullableInt(values.maxUses) : null,
    max_uses_per_buyer: values.hasMaxUsesPerBuyer
      ? Math.max(
          DEFAULT_MAX_USES_PER_BUYER,
          Math.trunc(values.maxUsesPerBuyer || DEFAULT_MAX_USES_PER_BUYER)
        )
      : null,
    starts_at: toNullableString(values.startsAt),
    expires_at: toNullableString(values.expiresAt),
    applies_to: COUPON_APPLIES_TO,
    status: values.status,
  };
}

/** A API devolve ISO 8601; os inputs de data trabalham com `YYYY-MM-DD`. */
export function toDateInputValue(isoDate: string | null): string {
  if (!isoDate) {
    return '';
  }

  return isoDate.slice(0, 10);
}

export function couponToFormValues(coupon: Coupon): CouponFormValues {
  return {
    name: coupon.name,
    discountType: coupon.discountType,
    discount: coupon.discount === null ? null : Number(coupon.discount),
    discountAmount: coupon.discountAmount,
    offerIds: coupon.offerIds ?? [],
    paymentMethods: coupon.paymentMethods ?? [],
    currency: couponCurrencyFromApi(coupon.currency) ?? 'BRL',
    hasMinOrderValue: (coupon.minOrderValue ?? 0) > 0,
    minOrderValue: coupon.minOrderValue ?? 0,
    hasMaxUses: coupon.maxUses !== null,
    maxUses: coupon.maxUses,
    hasMaxUsesPerBuyer: (coupon.maxUsesPerBuyer ?? null) !== null,
    maxUsesPerBuyer: coupon.maxUsesPerBuyer ?? DEFAULT_MAX_USES_PER_BUYER,
    startsAt: toDateInputValue(coupon.startsAt),
    expiresAt: toDateInputValue(coupon.expiresAt),
    status: coupon.status,
  };
}

/** `YYYY-MM-DD` no fuso local; `toISOString()` adiantaria o dia à noite. */
export function toLocalDateInputValue(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');

  return `${date.getFullYear()}-${month}-${day}`;
}
